import { ToggleSelect } from '@/components/ToggleSelect';
import { WithLoader } from '@/components/WithLoader';
import { CompetitorsHorizontalBarGraph } from '@/dashboards/CompetitorsMediaInvestment/components/CompetitorsHorizontalBarGraph';
import { formatMissingVariablesNotice } from '@/dashboards/CompetitorsMediaInvestment/helpers/formatMissingVariablesNotice';
import { useCompetitorsMediaInvestmentContext } from '@/dashboards/CompetitorsMediaInvestment/hooks/useCompetitorsMediaInvestmentContext';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { roundToDecimals } from '@/utils/numberUtils';
import { InfoIcon, Typography } from '@analytical-alley/ui';
import React, { useMemo } from 'react';

const formatter = new Intl.NumberFormat('fr-FR', {
    useGrouping: true,
    minimumFractionDigits: 0,
});

export const CompetitorMediaInvestmentSection = () => {
    const {
        isLoading,
        isFetching,
        competitorsMediaInvestmentData: {
            totalInvestment,
            totalInvestmentByVariable,
            variableData,
        },
        competitorsMediaEffectData,
        selectedCategoryFilter,
        missingVariables,
        getCompetitorImpact,
        setSelectedCategory,
    } = useCompetitorsMediaInvestmentContext();

    const {
        dashboard: { competitorVariableName },
    } = useDashboardGlobalContext();

    const isEmptyResults =
        totalInvestment === 0 &&
        Object.keys(totalInvestmentByVariable).length === 0;

    const sortedVariableData = useMemo(() => {
        return variableData.sort(
            (a, b) =>
                totalInvestmentByVariable.get(b.variable)! -
                totalInvestmentByVariable.get(a.variable)!,
        );
    }, [totalInvestmentByVariable, variableData]);

    const maxValue = useMemo(() => {
        const rawMax = Array.from(totalInvestmentByVariable.values()).reduce(
            (max, value) => Math.max(max, (value / totalInvestment) * 100),
            0,
        );
        return rawMax * 1.05;
    }, [totalInvestment, totalInvestmentByVariable]);

    const missingVariablesNotice = useMemo(
        () => formatMissingVariablesNotice(missingVariables),
        [missingVariables],
    );

    return (
        <div className="glass-tile p-6">
            <WithLoader isLoading={isLoading} isFetching={isFetching}>
                <div className="flex justify-between mb-4">
                    <Typography variant="titleS">
                        <span className="flex items-center gap-1">
                            Competitor Spend Share
                            {missingVariablesNotice && (
                                <InfoIcon>{missingVariablesNotice}</InfoIcon>
                            )}
                        </span>
                    </Typography>
                    {!isEmptyResults && (
                        <ToggleSelect
                            items={selectedCategoryFilter.map((item) => ({
                                label: item.name,
                                value: item.name,
                                checked: item.selected,
                            }))}
                            placement={'bottom-end'}
                            compact
                            label={'Media Category'}
                            showAllCheckbox={true}
                            onChange={(name, checked) => {
                                setSelectedCategory(name, checked);
                            }}
                        />
                    )}
                </div>
                {isEmptyResults && (
                    <div className="relative w-full">
                        <Typography
                            className="font-mono text-center"
                            variant="bodyM"
                        >
                            Competitor’s investments are not available yet.
                        </Typography>
                    </div>
                )}
                {!isEmptyResults && (
                    <CompetitorsHorizontalBarGraph maxValue={maxValue}>
                        {sortedVariableData.map((value) => {
                            const investment = totalInvestmentByVariable.get(
                                value.variable,
                            )!;
                            const percent = roundToDecimals(
                                (investment / totalInvestment) * 100,
                            );

                            return (
                                <CompetitorsHorizontalBarGraph.Row
                                    activeTitle={competitorVariableName}
                                    color={value.color}
                                    key={`${value.variable}-${value.variable}`}
                                    title={value.variable}
                                    impact={
                                        value.variable === competitorVariableName
                                            ? 'neutral'
                                            : getCompetitorImpact(value.variable)
                                    }
                                    impactValue={competitorsMediaEffectData.totalInvestmentByVariable.get(
                                        value.variable,
                                    )}
                                    maxValue={maxValue}
                                    percent={percent}
                                    value={formatter.format(investment)}
                                />
                            );
                        })}
                    </CompetitorsHorizontalBarGraph>
                )}
            </WithLoader>
        </div>
    );
};
